// EditPOModal.tsx - CORRECTED VERSION
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Autocomplete,
  Box,
  Typography
} from '@mui/material';
import { IMC_EXPENSE_CODES, IMC_INCOME_CODES } from '../../constants/imcCodes';

interface EditPOModalProps {
  open: boolean;
  onClose: () => void;
  purchaseOrder: any;
  onSave: (updatedData: any) => void;
}

const EditPOModal: React.FC<EditPOModalProps> = ({
  open,
  onClose,
  purchaseOrder,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    notes: '',
    expectedInvoices: [] as any[],
  });

  // Format IMC codes for autocomplete
  const imcCodeOptions = [...Object.values(IMC_EXPENSE_CODES), ...Object.values(IMC_INCOME_CODES)].map(code => ({
    value: code.code,
    label: `${code.code} - ${code.label.split(' - ').slice(1).join(' - ')}`,
    description: code.description,
  }));

  useEffect(() => {
    if (purchaseOrder) {
      console.log('Initializing form with PO data:', purchaseOrder);
      setFormData({
        notes: purchaseOrder.internalNotes || '',
        expectedInvoices: purchaseOrder.expectedInvoices.map((invoice: any) => ({
          ...invoice,
          imcCode: invoice.imcCode || '' // Ensure imcCode exists on each invoice
        }))
      });
    }
  }, [purchaseOrder]);

  const handleSubmit = () => {
    console.log('Submitting updated PO data:', formData);
    onSave(formData);
  };

  const handleInvoiceChange = (index: number, field: string, value: any) => {
    const updatedInvoices = [...formData.expectedInvoices];
    updatedInvoices[index] = {
      ...updatedInvoices[index],
      [field]: value
    };
    setFormData({
      ...formData,
      expectedInvoices: updatedInvoices
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Purchase Order</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom>
            PO Number: {purchaseOrder?.poNumber || 'N/A'}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Total Amount: €{purchaseOrder?.totalAmount?.toFixed(2) || '0.00'}
          </Typography>
        </Box>
        
        <Grid container spacing={3}>
          {/* Notes field */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              margin="normal"
            />
          </Grid>
          
          {/* Expected Invoices */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Expected Invoices
            </Typography>
            
            {formData.expectedInvoices.map((invoice, index) => (
              <Grid container spacing={2} key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Payee"
                    value={invoice.payee}
                    onChange={(e) => handleInvoiceChange(index, 'payee', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={invoice.description || ''}
                    onChange={(e) => handleInvoiceChange(index, 'description', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    value={invoice.amount}
                    onChange={(e) => handleInvoiceChange(index, 'amount', parseFloat(e.target.value) || 0)}
                    InputProps={{
                      startAdornment: <span>€</span>
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    value={imcCodeOptions.find(opt => opt.value === invoice.imcCode) || null}
                    onChange={(_, newValue) => {
                      handleInvoiceChange(index, 'imcCode', newValue ? newValue.value : '');
                    }}
                    options={imcCodeOptions}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField {...params} label="IMC Code" fullWidth />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body1">{option.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      </li>
                    )}
                  />
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPOModal;